import type { IntlShape } from 'react-intl';

import {
  OptimizeResult,
  OptimizeResultTag,
  OptimizeTableRow,
  OptimizeTestCase,
} from '../../types/optimize';

export const mapTagToLabel = (tag: OptimizeResultTag, intl: IntlShape) => {
  switch (tag) {
    case 'best_no_user_config_changes':
    case 'best_overall_no_user_config_changes':
    default:
      return intl.formatMessage({
        id: 'ReviewBoostUtils.currentInstructions',
        defaultMessage: 'Current Instructions',
        description: 'Label for current instructions optimization tag',
      });
    case 'best_overall':
      return intl.formatMessage({
        id: 'ReviewBoostUtils.highestAccuracy',
        defaultMessage: 'Highest Accuracy',
        description: 'Label for highest accuracy optimization tag',
      });
    case 'best_strictly_better':
      return intl.formatMessage({
        id: 'ReviewBoostUtils.strictlyBetter',
        defaultMessage: 'Strictly Better',
        description: 'Label for strictly better optimization tag',
      });
  }
};

export const mapTagToDescription = (
  tag: OptimizeResultTag,
  intl: IntlShape,
) => {
  switch (tag) {
    case 'best_no_user_config_changes':
    case 'best_overall_no_user_config_changes':
    default:
      return intl.formatMessage({
        id: 'ReviewBoostUtils.currentInstructionsDescription',
        defaultMessage:
          'The model has been optimized to improve accuracy while keeping your current instructions.',
        description: 'Description for current instructions optimization',
      });
    case 'best_overall':
      return intl.formatMessage({
        id: 'ReviewBoostUtils.highestAccuracyDescription',
        defaultMessage:
          'The model and instructions have been optimized to generate the highest accuracy.',
        description: 'Description for highest accuracy optimization',
      });
    case 'best_strictly_better':
      return intl.formatMessage({
        id: 'ReviewBoostUtils.strictlyBetterDescription',
        defaultMessage:
          'The model and instructions have been optimized to correct errors without affecting correct answers.',
        description: 'Description for strictly better optimization',
      });
  }
};

export const mapTestCaseOutcomeToStatus = (
  testCase: OptimizeTestCase,
  intl: IntlShape,
) => {
  const { outcome } = testCase;
  switch (outcome) {
    case 'correct':
      return {
        color: 'status.success',
        icon: 'status-success',
        tooltip: intl.formatMessage({
          id: 'ReviewBoostUtils.correct',
          defaultMessage: 'Correct',
          description: 'Tooltip for correct test case outcome',
        }),
      };
    case 'incorrect':
      return {
        color: 'status.danger',
        icon: 'status-danger',
        tooltip: intl.formatMessage({
          id: 'ReviewBoostUtils.incorrect',
          defaultMessage: 'Incorrect',
          description: 'Tooltip for incorrect test case outcome',
        }),
      };
    case 'partially_correct':
      return {
        color: 'status.warning',
        icon: 'status-warning',
        tooltip: intl.formatMessage({
          id: 'ReviewBoostUtils.partiallyCorrect',
          defaultMessage: 'Partially Correct',
          description: 'Tooltip for partially correct test case outcome',
        }),
      };
    default:
      return {
        color: 'status-info',
        icon: undefined,
        tooltip: undefined,
      };
  }
};

export const mapResultsToTableRows = (
  results: OptimizeResult[],
): OptimizeTableRow[] => {
  const rows: OptimizeTableRow[] = [];
  (results[0]?.testCases ?? []).forEach((testCase, index) => {
    const row: OptimizeTableRow = {
      id: (index + 1).toString(),
      entityId: testCase.entity.id,
    };
    results.forEach((result) => {
      row[result.tag] = result.testCases[index];
    });
    rows.push(row);
  });
  return rows;
};
